/**
 * The email composer (core MailComposerViewModel): From (my mailboxes), To / Cc / Bcc typed as text, subject, body,
 * attachments (uploaded first, then sent by file id) and Send. Opens from Compose, Reply / Reply all / Forward and a
 * contact's "Send email"; one at a time (a sheet, full screen on phones). Sending closes it with a toast. Replies and
 * forwards from a conversation open inline under its last message instead (MailReplyBox, Gmail's reply box): the
 * quoted message hidden behind "•••", the subject only on request, a docked window on Pop out, full screen on phones.
 */
import type { PickedFile } from "@bundle/core";
import { MailComposerViewModel, Shell, UNDO_SEND_MS, type ComposerStart } from "@bundle/core";
import type { MailTimePreset } from "@bundle/core/logic/mail";
import type { MailDraft, Mailbox, SendMailResponse } from "@bundle/contracts";
import type { FormatKind } from "@bundle/core/logic/composerFormat";
import * as Mail from "@bundle/core/logic/mail";
import { schedulePresets, signatureFor, snoozePresets, splitComposer, splitReplyText, type ReplyKind } from "@bundle/core/logic/mail";
import { sentWithLine, wantsSentWith } from "@bundle/core/logic/mailFooter";
import { fromNotSetUp } from "@bundle/core/logic/mailIdentity";
import { Avatar, Banner, Button, FullScreenPanel, MailBodyField, RecipientField, type MailBodyHandle, Chip, Divider, DockedPanel, EmojiPicker, FilePicker, Icon, IconButton, Menu, Popover, Pressable, Spinner, Stack, Text, TextField, Toast, clearMailUndo, closeComposer, mailFileSize, openComposer, useComposer, useFormat, useLocale, useMail, useMailUndo, useMediaQuery, useOwned, useT, useTr, useViewModel, useWorkspace, type MailServices, type MenuEntry } from "@bundle/ui";
import { myTimeZone } from "./mailMenus.ts";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/** Mounted once per workspace shell: shows the composer while one is open. */
export function MailComposerHost() {
  const open = useComposer();
  const mail = useMail();
  const [sent, setSent] = useState<SendMailResponse | undefined>(undefined);
  return (
    <>
      {open === undefined || mail === undefined ? null : <ComposerSheet key={open.seq} seq={open.seq} start={open.start} mail={mail} onSent={setSent} />}
      {mail === undefined ? null : <SentToast sent={sent} mail={mail} onClose={() => setSent(undefined)} />}
      <UndoToast />
    </>
  );
}

/**
 * After a send: "Message sent · Undo" while it waits in its undo window (Undo reopens it as a draft), or "Scheduled
 * for …" for a scheduled send.
 */
export function SentToast({ sent, mail, onClose }: { sent: SendMailResponse | undefined; mail: MailServices; onClose: () => void }) {
  const t = useT();
  const fmt = useFormat();
  const ws = useWorkspace();
  const m = sent?.message;
  const scheduledLater = m?.sendAt !== undefined && m.sendAt - ws.vm.deps.clock.now() > UNDO_SEND_MS + 2000;
  const undo = () => {
    if (m === undefined) return;
    onClose();
    void mail.mailbox.undoSend(m.id).then((draft) => {
      if (draft !== undefined) openComposer({ mailboxId: draft.mailboxId, mode: draft.mode, to: [], cc: [], subject: draft.subject, text: draft.text, draft });
    });
  };
  return (
    <Toast
      open={m !== undefined}
      onClose={onClose}
      message={scheduledLater && m?.sendAt !== undefined ? t("mail.compose.scheduled", { time: fmt.dateTime(m.sendAt) }) : t("mail.compose.sent")}
      {...(m?.status === "scheduled" ? { action: { label: t("mail.compose.undo"), onPress: undo, testID: "mail-sent-undo" } } : {})}
      closeLabel={t("common.close")}
      durationMs={scheduledLater ? 6000 : UNDO_SEND_MS}
      testID="mail-sent-toast"
    />
  );
}

/** "Conversation archived · Undo" after an action that moved conversations (showMailUndo). */
function UndoToast() {
  const t = useT();
  const u = useMailUndo();
  const undo = () => {
    u?.run();
    clearMailUndo();
  };
  return (
    <Toast
      key={u?.seq}
      open={u !== undefined}
      onClose={clearMailUndo}
      message={u?.message ?? ""}
      {...(u === undefined ? {} : { action: { label: t("mail.compose.undo"), onPress: undo, testID: "mail-undo" } })}
      closeLabel={t("common.close")}
      durationMs={8000}
      testID="mail-undo-toast"
    />
  );
}

/** Send now (after the undo window), or Gmail's "Schedule send" choices. */
function SendButton({ onSend, sending, disabled }: { onSend: (at?: number) => void; sending: boolean; disabled: boolean }) {
  const t = useT();
  const fmt = useFormat();
  const ws = useWorkspace();
  const presets: MenuEntry[] = schedulePresets(ws.vm.deps.clock.now(), myTimeZone()).map((p) => ({
    id: `schedule-${p.id}`,
    label: t("mail.snooze.option", { option: t(`mail.time.${p.id}`), time: fmt.dateTime(p.at) }),
    icon: "clock",
    onSelect: () => onSend(p.at),
  }));
  return (
    <Stack direction="horizontal" align="center" gap="0.5">
      <Button variant="primary" label={t("mail.compose.send")} icon="send" onPress={() => onSend()} loading={sending} disabled={disabled} testID="mail-compose-send" />
      <Menu items={presets} testID="mail-compose-schedule">
        <IconButton icon="chevronDown" variant="filled" accessibilityLabel={t("mail.compose.schedule")} onPress={() => undefined} disabled={disabled || sending} testID="mail-compose-schedule-button" />
      </Menu>
    </Stack>
  );
}

/** The view model and attachment uploads both composers share. */
function useComposerForm(key: string, start: ComposerStart, mail: MailServices, onSent: (sent: SendMailResponse) => void) {
  const ws = useWorkspace();
  const locale = useLocale();
  const boxes = useViewModel(mail.mailbox).mailboxes;
  // "Sent with Bundle" on this mailbox's messages: my setting, unless an admin turned it off.
  const sentWithFor = (mailbox: Mailbox | undefined) => wantsSentWith({ workspaceAllows: ws.state.cache?.workspace.mailSentWith, mailbox, removed: false });
  const signature = signatureFor(boxes.find((m) => m.id === start.mailboxId), start.mode);
  const sentWith = sentWithFor(boxes.find((m) => m.id === start.mailboxId));
  const create = useCallback(() => new MailComposerViewModel(ws.vm, start, signature, sentWith), [ws.vm, start, signature, sentWith]);
  const vm = useOwned(key, create);
  const s = useViewModel(vm);
  const [picking, setPicking] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [uploadFailed, setUploadFailed] = useState(false);
  const attach = (files: PickedFile[]) => {
    setPicking(false);
    for (const f of files) {
      setUploading((n) => n + 1);
      mail.uploadAttachment(f)
        .then((fileId) => vm.addAttachment({ fileId, name: f.name, size: f.size }))
        .catch(() => setUploadFailed(true))
        .finally(() => setUploading((n) => n - 1));
    }
  };
  // Sent at once it would leave no undo: it waits UNDO_SEND_MS (Gmail), or until the chosen time.
  const send = async (at?: number) => {
    // The language SentWithLine shows the line in, so the message carries the same one.
    const sent = await vm.send(at ?? Date.now() + UNDO_SEND_MS, locale);
    if (sent !== undefined) onSent(sent);
  };
  // The From's domain can't send yet (ownership verified, mail still elsewhere): say so and keep Send off.
  const box = boxes.find((m) => m.id === s.mailboxId);
  const notSetUp = box === undefined ? undefined : fromNotSetUp(box, s.from);
  return { vm, s, boxes, picking, setPicking, uploading, uploadFailed, setUploadFailed, attach, send, notSetUp, sentWithFor };
}

function ComposerSheet({ seq, start, mail, onSent }: { seq: number; start: ComposerStart; mail: MailServices; onSent: (sent: SendMailResponse) => void }) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const ws = useWorkspace();
  const people = useRecipientPeople(mail);
  const { vm, s, boxes, picking, setPicking, uploading, uploadFailed, setUploadFailed, attach, send, notSetUp, sentWithFor } = useComposerForm(`mailComposer:${seq}`, start, mail, (sent) => {
    onSent(sent);
    closeComposer();
  });
  useSendKey("mail-composer", () => {
    if (!s.sending && uploading === 0 && notSetUp === undefined) void send();
  });
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Spark's bottom row: the tools, or (T|) the formatting bar in their place.
  const [formatting, setFormatting] = useState(false);
  const [emoji, setEmoji] = useState(false);
  const [ai, setAi] = useState<"draft" | "ask" | undefined>(undefined);
  const parts = splitComposer(s.text);
  // The body is WYSIWYG (MailBodyField): the toolbar and the emoji picker act on its selection.
  const body = useRef<MailBodyHandle | null>(null);
  const format = (kind: FormatKind) => body.current?.format(kind);
  const insertEmoji = (e: string) => {
    setEmoji(false);
    body.current?.insert(e);
  };
  // Closing keeps what I wrote as a draft; Discard deletes it.
  const close = () => void vm.saveDraft().then(closeComposer);
  const discard = () => void vm.discard().then(closeComposer);
  const from = boxes.find((m) => m.id === s.mailboxId);
  const title = s.subject.trim() === "" ? t("mail.compose.new") : s.subject;
  const now = ws.vm.deps.clock.now();
  const fmt = useFormat();
  const timeLabel = (id: MailTimePreset["id"], at: number) => t("mail.snooze.option", { option: t(`mail.time.${id}`), time: fmt.dateTime(at) });
  // Every address I can send from (Gmail's "Send mail as"): each mailbox's sendAs (aliases, alias domains, groups).
  const fromAddress = s.from ?? from?.sendAs?.find((x) => x.default === true)?.address ?? from?.address ?? "";
  const fromChoices = boxes.flatMap((m) => (m.sendAs ?? [{ address: m.address, name: m.displayName }]).map((a) => ({ mailbox: m, address: a.address, name: a.name ?? m.displayName })));
  const fromMenu: MenuEntry[] = fromChoices.map((c) => ({
    id: `${c.mailbox.id}:${c.address}`,
    label: `${c.name} <${c.address}>`,
    ...(c.mailbox.id === s.mailboxId && c.address === fromAddress ? { icon: "check" as const } : {}),
    onSelect: () => {
      vm.setMailbox(c.mailbox.id, sentWithFor(c.mailbox));
      vm.setFrom(c.address);
    },
  }));
  const remindMenu: MenuEntry[] = [
    ...snoozePresets(now, myTimeZone()).map((p) => ({ id: `remind-${p.id}`, label: timeLabel(p.id, p.at), icon: "clock" as const, onSelect: () => vm.setRemind(p.at) })),
    ...(s.remindAt === undefined ? [] : [{ id: "remind-off", separator: true } as MenuEntry, { id: "remind-none", label: t("mail.compose.remindOff"), icon: "close" as const, onSelect: () => vm.setRemind(undefined) }]),
  ];
  const laterMenu: MenuEntry[] = schedulePresets(now, myTimeZone()).map((p) => ({ id: `later-${p.id}`, label: timeLabel(p.id, p.at), icon: "clock" as const, onSelect: () => void send(p.at) }));
  const templateMenu: MenuEntry[] = [
    ...(s.templates ?? []).map((tpl) => ({ id: `tpl-${tpl.id}`, label: tpl.name, icon: "notes" as const, onSelect: () => vm.applyTemplate(tpl) })),
    ...((s.templates ?? []).length > 0 ? [{ id: "tpl-sep", separator: true } as MenuEntry] : []),
    { id: "tpl-save", label: t("mail.compose.saveTemplate"), icon: "plus" as const, disabled: parts.body.trim() === "", onSelect: () => void vm.saveAsTemplate(s.subject.trim() === "" ? parts.body.split("\n")[0]!.slice(0, 60) : s.subject.trim()) },
  ];
  const translateMenu: MenuEntry[] = TRANSLATE_TO.map((l) => ({ id: `tr-${l.id}`, label: l.label, onSelect: () => void vm.assist("", "translate", l.id) }));
  const styleMenu: MenuEntry[] = (["bulletList", "orderedList", "quote", "code", "link"] as const).map((k) => ({ id: `style-${k}`, label: t(`composer.format.${k}`), onSelect: () => format(k) }));
  const aiAvailable = ws.state.cache?.aiAvailable === true;
  const aiMenu: MenuEntry[] = [
    { id: "ai-draft", label: t("mail.compose.ai.draft"), icon: "edit", onSelect: () => setAi("draft") },
    { id: "ai-ask", label: t("mail.compose.ai.ask"), icon: "sparkles", onSelect: () => setAi("ask") },
    { id: "ai-sep", separator: true },
    ...(["rewrite", "shorten", "lengthen", "formal", "friendly", "fix"] as const).map((a) => ({
      id: `ai-${a}`, label: t(`mail.compose.ai.${a}`), disabled: parts.body.trim() === "", onSelect: () => void vm.assist("", a),
    })),
  ];
  return (
    <DockedPanel
      title={title}
      minimized={minimized}
      expanded={expanded}
      onMinimize={() => setMinimized(!minimized)}
      onExpand={() => setExpanded(!expanded)}
      onClose={close}
      labels={{ minimize: t("mail.compose.minimize"), restore: t("mail.compose.restore"), expand: t("mail.compose.expand"), collapse: t("mail.compose.collapse"), close: t("common.close") }}
      footer={
        <>
          <IconButton icon="text" variant={formatting ? "filled" : "ghost"} accessibilityLabel={t("mail.compose.formatting")} onPress={() => setFormatting(!formatting)} testID="mail-compose-formatting" />
          <Stack grow direction="horizontal" align="center" gap="0.5" paddingX="1.5" radius="full" background="bg.sunken" testID={formatting ? "mail-compose-format" : "mail-compose-tools"}>
            {formatting ? (
              <>
                <IconButton icon="check" accessibilityLabel={t("mail.compose.formatDone")} onPress={() => setFormatting(false)} testID="mail-compose-format-done" />
                {aiAvailable ? (
                  <Menu items={translateMenu} testID="mail-compose-translate">
                    <IconButton icon="language" accessibilityLabel={t("mail.compose.translate")} onPress={() => undefined} disabled={parts.body.trim() === "" || s.assisting} testID="mail-compose-translate-button" />
                  </Menu>
                ) : null}
                <Menu items={styleMenu} testID="mail-compose-style">
                  <IconButton icon="format" accessibilityLabel={t("mail.compose.textStyle")} onPress={() => undefined} testID="mail-compose-style-button" />
                </Menu>
                <IconButton icon="bold" accessibilityLabel={t("composer.format.bold")} onPress={() => format("bold")} testID="mail-compose-bold" />
                <IconButton icon="italic" accessibilityLabel={t("composer.format.italic")} onPress={() => format("italic")} testID="mail-compose-italic" />
                <IconButton icon="strike" accessibilityLabel={t("composer.format.strike")} onPress={() => format("strike")} testID="mail-compose-strike" />
                <Popover
                  open={emoji}
                  onClose={() => setEmoji(false)}
                  side="top"
                  align="start"
                  anchor={<IconButton icon="emoji" accessibilityLabel={t("composer.emoji")} onPress={() => setEmoji(true)} testID="mail-compose-emoji" />}
                >
                  <EmojiPicker locale={locale === "pt-BR" ? "pt-BR" : "en"} searchPlaceholder={t("picker.emoji.search")} onSelect={insertEmoji} />
                </Popover>
              </>
            ) : (
              <>
                <IconButton icon="attach" accessibilityLabel={t("mail.compose.attach")} onPress={() => setPicking(true)} testID="mail-compose-attach" />
                <Menu items={templateMenu} testID="mail-compose-templates">
                  <IconButton icon="notes" accessibilityLabel={t("mail.compose.templates")} onPress={() => void (s.templates === undefined ? vm.loadTemplates() : undefined)} testID="mail-compose-templates-button" />
                </Menu>
                <Menu items={remindMenu} testID="mail-compose-remind">
                  <IconButton icon="bell" variant={s.remindAt === undefined ? "ghost" : "filled"} accessibilityLabel={t("mail.compose.remind")} onPress={() => undefined} testID="mail-compose-remind-button" />
                </Menu>
                <Menu items={laterMenu} testID="mail-compose-later">
                  <IconButton icon="clock" accessibilityLabel={t("mail.compose.schedule")} onPress={() => undefined} disabled={s.sending || uploading > 0 || notSetUp !== undefined} testID="mail-compose-schedule-button" />
                </Menu>
                <AddContact mail={mail} onAdd={(address) => vm.setTo(s.to.trim() === "" ? address : `${s.to.replace(/[,\s]+$/, "")}, ${address}`)} />
              </>
            )}
          </Stack>
          {aiAvailable ? (
            <Menu items={aiMenu} align="end" testID="mail-compose-ai">
              <Button variant="secondary" size="sm" icon="sparkles" label={t("mail.compose.ai")} onPress={() => undefined} loading={s.assisting} testID="mail-compose-ai-button" />
            </Menu>
          ) : null}
        </>
      }
      testID="mail-composer"
    >
      {/* Spark's header: From in the middle (▾ picks another of my addresses), Send on the right. */}
      <Stack direction="horizontal" align="center" gap="2" paddingY="1.5">
        <Stack grow align="center">
          {fromChoices.length > 1 ? (
            <Menu items={fromMenu} testID="mail-compose-from">
              <Pressable direction="horizontal" align="center" gap="1" onPress={() => undefined} accessibilityLabel={t("mail.compose.from")} testID="mail-compose-from-button">
                <Text weight="medium" lines={1}>
                  {fromAddress}
                </Text>
                <Icon name="chevronDown" size="icon.sm" color="text.secondary" />
              </Pressable>
            </Menu>
          ) : (
            <Text weight="medium" lines={1} testID="mail-compose-from">
              {fromAddress}
            </Text>
          )}
        </Stack>
        <IconButton icon="send" variant="filled" accessibilityLabel={t("mail.compose.send")} onPress={() => void send()} disabled={s.sending || uploading > 0 || notSetUp !== undefined} testID="mail-compose-send" />
      </Stack>
      <ComposeLine label={t("mail.compose.toLabel")}>
        <RecipientField {...people} value={s.to} onChange={(v) => vm.setTo(v)} accessibilityLabel={t("mail.compose.to")} autoFocus={start.mode !== "reply" && start.draft === undefined} testID="mail-compose-to" />
        {s.showCc ? null : <Chip label={`${t("mail.compose.cc")}: ${t("mail.compose.bcc")}:`} tone="accent" onPress={() => vm.showCcBcc()} testID="mail-compose-show-cc" />}
      </ComposeLine>
      {s.showCc ? (
        <>
          <ComposeLine label={`${t("mail.compose.cc")}:`}>
            <RecipientField {...people} value={s.cc} onChange={(v) => vm.setCc(v)} accessibilityLabel={t("mail.compose.cc")} testID="mail-compose-cc" />
          </ComposeLine>
          <ComposeLine label={`${t("mail.compose.bcc")}:`}>
            <RecipientField {...people} value={s.bcc} onChange={(v) => vm.setBcc(v)} accessibilityLabel={t("mail.compose.bcc")} testID="mail-compose-bcc" />
          </ComposeLine>
        </>
      ) : null}
      <ComposeLine label={t("mail.compose.subjectLabel")}>
        <TextField value={s.subject} onChange={(v) => vm.setSubject(v)} accessibilityLabel={t("mail.compose.subject")} variant="plain" testID="mail-compose-subject" />
      </ComposeLine>
      <Stack grow paddingY="2">
        <MailBodyField
          placeholder={t("mail.compose.saySomething")}
          accessibilityLabel={t("mail.compose.body")}
          value={parts.body}
          onChange={(v) => vm.setBody(v)}
          handleRef={body}
          autoFocus={start.mode === "reply" || start.draft !== undefined}
          testID="mail-compose-body"
        />
      </Stack>
      <SignatureBlock
        signature={parts.signature}
        onRemove={() => vm.removeSignature()}
        onEdit={() => {
          // The composer lives outside the shell's navigation: go there the way the address bar does.
          window.history.pushState(null, "", Shell.formatUrl({ type: "settings", ws: ws.slug, section: "mail" }));
          window.dispatchEvent(new PopStateEvent("popstate"));
        }}
        {...(s.sentWith === "removed" ? { onAddSentWith: () => vm.addSentWith() } : {})}
      />
      {s.sentWith === "on" ? <SentWithLine onRemove={() => vm.removeSentWith()} /> : null}
      {ai === undefined ? null : <AssistBar vm={vm} assisting={s.assisting} mode={ai} onDone={() => setAi(undefined)} />}
      {s.remindAt === undefined ? null : (
        <Stack direction="horizontal">
          <Chip icon="bell" label={t("mail.compose.remindAt", { time: fmt.dateTime(s.remindAt) })} onRemove={() => vm.setRemind(undefined)} removeLabel={t("mail.compose.remindOff")} testID="mail-compose-remind-chip" />
        </Stack>
      )}
      {s.attachments.length > 0 ? (
        <Stack direction="horizontal" gap="2" wrap paddingY="2">
          {s.attachments.map((a) => (
            <Chip key={a.fileId} icon="attach" label={`${a.name} · ${mailFileSize(a.size, locale)}`} onRemove={() => vm.removeAttachment(a.fileId)} removeLabel={t("mail.compose.removeAttachment")} testID={`mail-compose-file-${a.fileId}`} />
          ))}
        </Stack>
      ) : null}
      {notSetUp === undefined ? null : (
        <Text color="text.secondary" variant="footnote" testID="mail-compose-not-set-up">
          {t("mail.compose.sendingNotSetUp", { domain: notSetUp })}
        </Text>
      )}
      {s.error === undefined ? null : (
        <Text color="status.danger.fg" variant="footnote" testID="mail-compose-error">
          {tr(s.error)}
        </Text>
      )}
      {s.invalid.length > 0 ? (
        <Text color="status.danger.fg" variant="footnote">
          {s.invalid.join(", ")}
        </Text>
      ) : null}
      <Stack direction="horizontal" align="center" justify="end" gap="2">
        {s.saveFailed !== true && s.savedAt === undefined ? null : (
          <Text variant="caption" color={s.saveFailed === true ? "status.danger.fg" : "text.tertiary"} testID="mail-compose-saved">
            {t(s.saveFailed === true ? "mail.compose.saveFailed" : "mail.compose.saved")}
          </Text>
        )}
        <IconButton icon="trash" size="sm" accessibilityLabel={t("mail.compose.discard")} onPress={discard} testID="mail-compose-discard" />
      </Stack>
      <FilePicker open={picking} multiple onPick={attach} onClose={() => setPicking(false)} testID="mail-compose-picker" />
      <Toast open={uploadFailed} onClose={() => setUploadFailed(false)} message={t("mail.compose.failed")} closeLabel={t("common.close")} durationMs={5000} testID="mail-compose-upload-failed" />
    </DockedPanel>
  );
}

/** Languages the composer translates to (the formatting bar's translate button). */
const TRANSLATE_TO = [
  { id: "English", label: "English" },
  { id: "Português (Brasil)", label: "Português" },
  { id: "Español", label: "Español" },
  { id: "Français", label: "Français" },
  { id: "Deutsch", label: "Deutsch" },
  { id: "Italiano", label: "Italiano" },
];

/**
 * Spark's signature block: my signature (or "No signature") with ••• (edit in Settings; "Add 'Sent with Bundle'" after
 * its ✕) and ✕ (leave it off).
 */
function SignatureBlock({ signature, onRemove, onEdit, onAddSentWith }: { signature: string | undefined; onRemove: () => void; onEdit: () => void; onAddSentWith?: () => void }) {
  const t = useT();
  const menu: MenuEntry[] = [
    { id: "sig-edit", label: t("mail.compose.signatureEdit"), icon: "edit", onSelect: onEdit },
    ...(onAddSentWith === undefined ? [] : [{ id: "sig-sent-with", label: t("mail.compose.sentWithAdd"), icon: "plus" as const, onSelect: onAddSentWith }]),
  ];
  return (
    <Stack direction="horizontal" align="start" gap="2" padding="2" radius="md" border="border.subtle" hoverGroup testID="mail-compose-signature">
      <Stack grow>
        <Text color={signature === undefined ? "text.tertiary" : "text.secondary"} selectable>
          {signature ?? t("mail.compose.noSignature")}
        </Text>
      </Stack>
      <Menu items={menu} align="end" testID="mail-compose-signature-menu">
        <IconButton icon="more" size="sm" accessibilityLabel={t("mail.compose.signatureOptions")} onPress={() => undefined} testID="mail-compose-signature-more" />
      </Menu>
      {signature === undefined ? null : <IconButton icon="close" size="sm" accessibilityLabel={t("mail.compose.signatureRemove")} onPress={onRemove} testID="mail-compose-signature-remove" />}
    </Stack>
  );
}

/**
 * "Sent with Bundle · …" under my signature, as recipients will see it (core logic/mailFooter; the server adds it at
 * send time): grey, not editable; ✕ (on hover) leaves it off this message.
 */
function SentWithLine({ onRemove }: { onRemove: () => void }) {
  const t = useT();
  const locale = useLocale();
  return (
    <Stack direction="horizontal" align="center" gap="1.5" paddingX="2" radius="md" hoverGroup hoverHighlight testID="mail-compose-sent-with">
      <Text variant="footnote" color="text.tertiary" lines={1}>
        {sentWithLine(locale).text}
      </Text>
      <Stack revealOnHover>
        <IconButton icon="close" size="sm" accessibilityLabel={t("mail.compose.sentWithRemove")} onPress={onRemove} testID="mail-compose-sent-with-remove" />
      </Stack>
    </Stack>
  );
}

/** Add someone from my contacts to To (the toolbar's add-contact button). */
function AddContact({ mail, onAdd }: { mail: MailServices; onAdd: (address: string) => void }) {
  const t = useT();
  const ws = useWorkspace();
  const c = useViewModel(mail.contacts);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const show = () => {
    setOpen(true);
    if (c.status !== "ready") void mail.contacts.refresh();
  };
  const needle = q.trim().toLowerCase();
  const found = c.all.filter((x) => needle === "" || x.email.includes(needle) || (x.name ?? "").toLowerCase().includes(needle)).slice(0, 8);
  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      side="top"
      align="end"
      anchor={<IconButton icon="user" accessibilityLabel={t("mail.compose.addContact")} onPress={show} testID="mail-compose-add-contact" />}
    >
      <Stack gap="1" padding="2" width="tab.maxWidth" testID="mail-compose-contacts">
        <TextField value={q} onChange={setQ} placeholder={t("mail.compose.findContact")} accessibilityLabel={t("mail.compose.findContact")} autoFocus testID="mail-compose-contact-search" />
        {found.map((x) => (
          <Pressable
            key={x.id}
            direction="horizontal"
            align="center"
            gap="2"
            padding="1.5"
            radius="sm"
            hoverHighlight
            onPress={() => {
              onAdd(x.name === undefined ? x.email : `${x.name} <${x.email}>`);
              setOpen(false);
              setQ("");
            }}
            testID={`mail-compose-contact-${x.id}`}
          >
            <Avatar name={x.name ?? x.email} src={Mail.mailAvatarPath(ws.vm.wsId, x.email)} size="avatar.sm" />
            <Stack grow>
              <Text lines={1}>{x.name ?? x.email}</Text>
              {x.name === undefined ? null : (
                <Text variant="caption" color="text.secondary" lines={1}>
                  {x.email}
                </Text>
              )}
            </Stack>
          </Pressable>
        ))}
      </Stack>
    </Popover>
  );
}

/** The inline reply box keeps its always-open "Help me write" line (only where the server has AI). */
function ReplyAssist({ vm, assisting }: { vm: MailComposerViewModel; assisting: boolean }) {
  const ws = useWorkspace();
  if (ws.state.cache?.aiAvailable !== true) return null;
  return <AssistBar vm={vm} assisting={assisting} mode="draft" onDone={() => undefined} autoFocus={false} />;
}

/** "+ai": Generate a Draft (from a description) or Ask AI (a request about the draft); Enter runs it. */
function AssistBar({ vm, assisting, mode, onDone, autoFocus = true }: { vm: MailComposerViewModel; assisting: boolean; mode: "draft" | "ask"; onDone: () => void; autoFocus?: boolean }) {
  const t = useT();
  const tr = useTr();
  const err = useViewModel(vm).assistError;
  const [prompt, setPrompt] = useState("");
  const go = () => {
    if (prompt.trim() !== "") void vm.assist(prompt, mode).then(() => {
      setPrompt("");
      if (vm.state.assistError === undefined) onDone();
    });
  };
  return (
    <Stack gap="1" paddingY="1.5">
      <Stack direction="horizontal" align="center" gap="2" paddingX="3" radius="full" background="bg.sunken">
        {assisting ? <Spinner size="icon.sm" /> : <Icon name="sparkles" size="icon.sm" color="text.secondary" />}
        <Stack grow>
          <TextField
            value={prompt}
            onChange={setPrompt}
            onSubmit={go}
            placeholder={assisting ? t("mail.compose.assisting") : mode === "draft" ? t("mail.compose.assist") : t("mail.compose.ai.askPlaceholder")}
            accessibilityLabel={mode === "draft" ? t("mail.compose.ai.draft") : t("mail.compose.ai.ask")}
            variant="plain"
            autoFocus={autoFocus}
            disabled={assisting}
            testID="mail-compose-assist"
          />
        </Stack>
        {autoFocus ? <IconButton icon="close" size="sm" accessibilityLabel={t("common.close")} onPress={onDone} testID="mail-compose-assist-close" /> : null}
      </Stack>
      {err === undefined ? null : (
        <Text variant="caption" color="status.danger.fg">
          {tr(err)}
        </Text>
      )}
    </Stack>
  );
}

/** One of the composer's lines: its name (From, To…) then the field, a hairline under it. */
/** What every To / Cc / Bcc line shares: my contacts, loaded when a composer opens. */
function useRecipientPeople(mail: MailServices) {
  const ws = useWorkspace();
  const c = useViewModel(mail.contacts);
  const ready = c.status === "ready";
  useEffect(() => {
    if (!ready) void mail.contacts.refresh();
  }, [mail.contacts, ready]);
  return {
    contacts: c.all,
    now: ws.vm.deps.clock.now(),
    avatarUrl: (email: string) => Mail.mailAvatarPath(ws.vm.wsId, email),
  };
}

function ComposeLine({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <Stack>
      <Stack direction="horizontal" align="center" gap="2" paddingY="1.5">
        {label === undefined ? null : (
          <Text color="text.secondary" lines={1}>
            {label}
          </Text>
        )}
        <Stack grow direction="horizontal" align="center" gap="2">
          {children}
        </Stack>
      </Stack>
      <Divider />
    </Stack>
  );
}


/** Reply opens full screen at this width, and pop out isn't offered (the docked window fills a phone anyway). */
const PHONE = "(max-width: 640px)";
const APPLE = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** ⌘Enter / Ctrl+Enter sends from anywhere inside the composer called `root` (its testID). */
function useSendKey(root: string, send: () => void) {
  const latest = useRef(send);
  useEffect(() => {
    latest.current = send;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      if ((e.target as HTMLElement | null)?.closest?.(`[data-testid="${root}"]`) == null) return;
      e.preventDefault();
      e.stopPropagation();
      latest.current();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [root]);
}

/** A To / Cc / Bcc / From / Subject line of the reply box: the name in a narrow column, then the field. */
function ReplyLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="horizontal" align="center" gap="2">
      <Stack width="avatar.lg">
        <Text variant="footnote" color="text.tertiary" lines={1}>
          {label}
        </Text>
      </Stack>
      <Stack grow>{children}</Stack>
    </Stack>
  );
}

type ReplyBoxProps = {
  seq: number;
  start: ComposerStart;
  kind: ReplyKind;
  /** Changes when Reply is pressed again on the open box: the keyboard goes back to it. */
  nudge: number;
  mail: MailServices;
  onKind: (kind: ReplyKind) => void;
  onLeave: (sourceMessageId: string | undefined, draft: MailDraft | undefined) => void;
  onClose: () => void;
  onSent: (sent: SendMailResponse) => void;
  /** Filled while the box is open: saves what's in it, and says whether the thread can safely go away. */
  flushRef: { current: (() => Promise<boolean>) | undefined };
};

/**
 * Gmail's reply box. Under a conversation's last message: the reply type, who it goes to on one line (pressing it opens
 * From, To, Cc and a Bcc link), the message, the quoted original behind "•••", Send. Pop out moves the same box to a
 * docked window and leaves a note here; closing the window brings it back. On a phone it takes the whole screen.
 */
export function MailReplyBox({ seq, start, kind, nudge, mail, onKind, onLeave, onClose, onSent, flushRef }: ReplyBoxProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const people = useRecipientPeople(mail);
  const { vm, s, boxes, picking, setPicking, uploading, uploadFailed, setUploadFailed, attach, send, notSetUp } = useComposerForm(`mailReply:${seq}`, start, mail, onSent);
  const { main, quote } = splitReplyText(s.text);
  const phone = useMediaQuery(PHONE);
  const [popped, setPopped] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [editSubject, setEditSubject] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const body = useRef<MailBodyHandle | null>(null);
  useEffect(() => vm.switchReply(kind), [vm, kind]);
  // Closing keeps what I wrote: saved now, and the thread remembers it so Reply brings it back.
  const leave = useRef(onLeave);
  useEffect(() => {
    leave.current = onLeave;
  }, [onLeave]);
  useEffect(
    () => () => {
      void vm.saveDraft();
      leave.current(vm.state.sourceMessageId, vm.leftDraft());
    },
    [vm],
  );
  useEffect(() => {
    flushRef.current = () => vm.flushDraft();
    return () => {
      flushRef.current = undefined;
    };
  }, [flushRef, vm]);
  // Focus after the click that opened the box has finished (it would take the focus back to its button). Each change
  // of place (popped out, shown here) mounts the fields again, so the keyboard follows.
  const [focus, setFocus] = useState<{ seq: number; field: "body" | "to" } | undefined>(undefined);
  const focusOn = (field: "body" | "to") => {
    setTimeout(() => setFocus((f) => ({ seq: (f?.seq ?? 0) + 1, field })), 0);
  };
  useEffect(() => focusOn(start.kind === "forward" || kind === "forward" ? "to" : "body"), [nudge, popped, phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const noReply = Mail.noReplyRecipient(s.to);
  const kinds: { id: ReplyKind; label: string; icon: "reply" | "users" | "arrowRight" }[] = [
    { id: "reply", label: t("mail.action.reply"), icon: "reply" },
    { id: "replyAll", label: t("mail.action.replyAll"), icon: "users" },
    { id: "forward", label: t("mail.action.forward"), icon: "arrowRight" },
  ];
  const shown = kinds.find((k) => k.id === (s.replyKind ?? kind)) ?? kinds[0]!;
  const invalid = Mail.invalidRecipientCount([s.to, s.cc, s.bcc]);
  const openRecipients = showRecipients || shown.id === "forward" || s.to.trim() === "" || invalid > 0;
  const bccOpen = showBcc || s.bcc !== "";
  const canSend = uploading === 0 && notSetUp === undefined && !s.sending;
  useSendKey("mail-reply-box", () => {
    if (canSend) void send();
  });

  const box = boxes.find((m) => m.id === s.mailboxId);
  const sendAs = box?.sendAs ?? (box === undefined ? [] : [{ address: box.address, name: box.displayName }]);
  const fromAddress = s.from ?? sendAs.find((x) => x.default === true)?.address ?? box?.address ?? "";
  // A reply stays in its mailbox: From picks among that mailbox's addresses.
  const fromPicker =
    sendAs.length > 1 ? (
      <Menu items={sendAs.map((a) => ({ id: a.address, label: a.name === undefined ? a.address : `${a.name} <${a.address}>`, selected: a.address === fromAddress, onSelect: () => vm.setFrom(a.address) }))} testID="mail-reply-from">
        <Pressable direction="horizontal" align="center" gap="1" onPress={() => undefined} accessibilityLabel={t("mail.compose.from")} testID="mail-reply-from-button">
          <Text variant="footnote" lines={1}>
            {fromAddress}
          </Text>
          <Icon name="chevronDown" size="icon.sm" color="text.secondary" />
        </Pressable>
      </Menu>
    ) : (
      <Text variant="footnote" lines={1} testID="mail-reply-from">
        {fromAddress}
      </Text>
    );
  const bccLink = bccOpen ? null : <Button variant="ghost" size="sm" label={t("mail.compose.bcc")} onPress={() => setShowBcc(true)} testID="mail-reply-show-bcc" />;
  const menu: MenuEntry[] = [
    ...kinds.map((k) => ({ id: k.id, label: k.label, icon: k.icon, selected: k.id === shown.id, onSelect: () => onKind(k.id) })),
    { id: "sep", separator: true },
    { id: "subject", label: t("mail.reply.editSubject"), icon: "edit" as const, onSelect: () => setEditSubject(true) },
    ...(phone || popped ? [] : [{ id: "pop", label: t("mail.reply.popOut"), icon: "externalLink" as const, onSelect: () => setPopped(true) }]),
  ];
  const kindMenu = (
    <Menu items={menu} testID="mail-reply-kind">
      <Button variant="ghost" size="sm" icon={shown.icon} label={shown.label} aria-label={t("mail.reply.kind", { kind: shown.label })} onPress={() => undefined} testID="mail-reply-kind-button" />
    </Menu>
  );
  const openFields = () => {
    setShowRecipients(true);
    focusOn("to");
  };
  const summary = (
    <Pressable onPress={openFields} accessibilityLabel={t("mail.reply.editRecipients")} testID="mail-reply-recipients">
      <Text variant="footnote" color="text.secondary" lines={1} align="start">
        {Mail.recipientSummary([s.to, s.cc, s.bcc], popped ? 2 : undefined)}
      </Text>
    </Pressable>
  );
  const fields = openRecipients ? (
    <Stack gap="1.5">
      {phone || popped ? (
        <ReplyLine label={t("mail.compose.from")}>
          <Stack direction="horizontal" align="center" justify="between" gap="2">
            {fromPicker}
            {bccLink}
          </Stack>
        </ReplyLine>
      ) : null}
      <ReplyLine label={t("mail.compose.to")}>
        <RecipientField {...people} value={s.to} onChange={(v) => vm.setTo(v)} accessibilityLabel={t("mail.compose.to")} {...(focus?.field === "to" ? { focusSeq: focus.seq } : {})} testID="mail-compose-to" />
      </ReplyLine>
      <ReplyLine label={t("mail.compose.cc")}>
        <RecipientField {...people} value={s.cc} onChange={(v) => vm.setCc(v)} accessibilityLabel={t("mail.compose.cc")} testID="mail-compose-cc" />
      </ReplyLine>
      {bccOpen ? (
        <ReplyLine label={t("mail.compose.bcc")}>
          <RecipientField {...people} value={s.bcc} onChange={(v) => vm.setBcc(v)} accessibilityLabel={t("mail.compose.bcc")} {...(showBcc ? { focusSeq: 1 } : {})} testID="mail-compose-bcc" />
        </ReplyLine>
      ) : null}
    </Stack>
  ) : null;
  const subject = editSubject ? (
    <ReplyLine label={t("mail.compose.subject")}>
      <TextField value={s.subject} onChange={(v) => vm.setSubject(v)} accessibilityLabel={t("mail.compose.subject")} variant="plain" autoFocus testID="mail-compose-subject" />
    </ReplyLine>
  ) : null;
  const message = <MailBodyField placeholder={t("mail.compose.body")} accessibilityLabel={t("mail.compose.body")} value={main} onChange={(v) => vm.setMain(v)} handleRef={body} {...(focus?.field === "body" ? { focus: { offset: main.length, seq: focus.seq } } : {})} testID="mail-compose-body" />;
  const quoteToggle = quote === "" || showQuote ? null : <QuoteToggle onPress={() => setShowQuote(true)} testID="mail-compose-show-quote" />;
  const invalidNote =
    invalid === 0 ? null : (
      <Text color="status.danger.fg" variant="footnote" lines={1} testID="mail-compose-invalid">
        {t("mail.compose.invalidRecipients", { count: invalid })}
      </Text>
    );
  const notes = (
    <>
      {s.sentWith === "on" ? <SentWithLine onRemove={() => vm.removeSentWith()} /> : null}
      {quote === "" || !showQuote ? null : (
        <Stack gap="1">
          <TextField value={quote} onChange={(v) => vm.setQuote(v)} multiline accessibilityLabel={t("mail.showQuoted")} variant="plain" testID="mail-compose-quote" />
          <Stack direction="horizontal">
            <Button variant="ghost" size="sm" label={t("mail.compose.removeQuote")} onPress={() => vm.removeQuote()} testID="mail-compose-remove-quote" />
          </Stack>
        </Stack>
      )}
      <ReplyAssist vm={vm} assisting={s.assisting} />
      {s.attachments.length > 0 ? (
        <Stack direction="horizontal" gap="2" wrap>
          {s.attachments.map((a) => (
            <Chip key={a.fileId} icon="attach" label={`${a.name} · ${mailFileSize(a.size, locale)}`} onRemove={() => vm.removeAttachment(a.fileId)} removeLabel={t("mail.compose.removeAttachment")} testID={`mail-compose-file-${a.fileId}`} />
          ))}
        </Stack>
      ) : null}
      {notSetUp === undefined ? null : (
        <Text color="text.secondary" variant="footnote" testID="mail-compose-not-set-up">
          {t("mail.compose.sendingNotSetUp", { domain: notSetUp })}
        </Text>
      )}
      {shown.id === "forward" || noReply === undefined ? null : (
        <Text color="text.secondary" variant="footnote" testID="mail-compose-no-reply">
          {t("mail.compose.noReply", { address: noReply })}
        </Text>
      )}
      {s.error === undefined ? null : (
        <Text color="status.danger.fg" variant="footnote" testID="mail-compose-error">
          {tr(s.error)}
        </Text>
      )}
      {invalidNote}
    </>
  );
  const saved =
    s.saveFailed !== true && s.savedAt === undefined ? null : (
      <Stack fixedSize>
        <Text variant="caption" color={s.saveFailed === true ? "status.danger.fg" : "text.tertiary"} testID="mail-compose-saved">
          {t(s.saveFailed === true ? "mail.compose.saveFailed" : "mail.compose.saved")}
        </Text>
      </Stack>
    );
  const attachButton = <IconButton icon="attach" accessibilityLabel={t("mail.compose.attach")} onPress={() => setPicking(true)} testID="mail-compose-attach" />;
  const discard = <IconButton icon="trash" accessibilityLabel={t("mail.compose.discard")} onPress={() => void vm.discard().then(onClose)} testID="mail-compose-discard" />;
  const sendButton = <SendButton onSend={(at) => void send(at)} sending={s.sending} disabled={uploading > 0 || notSetUp !== undefined} />;
  const actions = (
    <Stack grow direction="horizontal" align="center" gap="2">
      {sendButton}
      <Stack fixedSize>
        <Text variant="caption" color="text.tertiary">
          {t("mail.reply.sendKey", { mod: APPLE ? "⌘" : "Ctrl+" })}
        </Text>
      </Stack>
      {attachButton}
      {quoteToggle}
      <Stack grow />
      {saved}
      {discard}
    </Stack>
  );
  const dialogs = (
    <>
      <FilePicker open={picking} multiple onPick={attach} onClose={() => setPicking(false)} testID="mail-compose-picker" />
      <Toast open={uploadFailed} onClose={() => setUploadFailed(false)} message={t("mail.compose.failed")} closeLabel={t("common.close")} durationMs={5000} testID="mail-compose-upload-failed" />
    </>
  );

  if (phone) {
    return (
      <FullScreenPanel
        accessibilityLabel={shown.label}
        testID="mail-reply-box"
        header={
          <>
            <IconButton icon="close" accessibilityLabel={t("common.close")} onPress={onClose} testID="mail-reply-close" />
            <Stack grow direction="horizontal">
              {kindMenu}
            </Stack>
            {sendButton}
          </>
        }
        footer={
          <Stack paddingX="2" gap="1">
            {formatting ? (
              <Stack direction="horizontal" align="center" gap="0.5">
                <IconButton icon="bold" accessibilityLabel={t("composer.format.bold")} onPress={() => body.current?.format("bold")} testID="mail-compose-bold" />
                <IconButton icon="italic" accessibilityLabel={t("composer.format.italic")} onPress={() => body.current?.format("italic")} testID="mail-compose-italic" />
                <IconButton icon="strike" accessibilityLabel={t("composer.format.strike")} onPress={() => body.current?.format("strike")} testID="mail-compose-strike" />
                <IconButton icon="list" accessibilityLabel={t("composer.format.bulletList")} onPress={() => body.current?.format("bulletList")} testID="mail-compose-bullets" />
                <IconButton icon="link" accessibilityLabel={t("composer.format.link")} onPress={() => body.current?.format("link")} testID="mail-compose-link" />
              </Stack>
            ) : null}
            <Stack direction="horizontal" align="center" gap="1">
              {attachButton}
              <IconButton icon="text" variant={formatting ? "filled" : "ghost"} accessibilityLabel={t("mail.compose.formatting")} onPress={() => setFormatting(!formatting)} testID="mail-compose-formatting" />
              <Stack grow />
              {saved}
              {discard}
            </Stack>
          </Stack>
        }
      >
        {openRecipients ? (
          <Stack padding="3" gap="1.5">
            {fields}
          </Stack>
        ) : (
          <Pressable onPress={openFields} accessibilityLabel={t("mail.reply.editRecipients")} testID="mail-reply-recipients">
            <Stack direction="horizontal" align="center" gap="2" paddingX="4" paddingY="3">
              <Text color="text.tertiary">{t("mail.compose.to")}</Text>
              <Stack grow>
                <Text color="text.secondary" lines={1} align="start">
                  {Mail.recipientSummary([s.to, s.cc, s.bcc], 1)}
                </Text>
              </Stack>
              <Icon name="chevronDown" size="icon.sm" color="text.secondary" />
            </Stack>
          </Pressable>
        )}
        <Divider />
        <Stack grow padding="4" gap="3">
          {subject}
          {message}
          {quoteToggle}
          {notes}
        </Stack>
        {dialogs}
      </FullScreenPanel>
    );
  }
  if (popped) {
    return (
      <>
        <Banner tone="neutral" icon="externalLink" message={t("mail.reply.popped")} action={{ label: t("mail.reply.showHere"), onPress: () => setPopped(false), testID: "mail-reply-show-here" }} testID="mail-reply-popped" />
        <DockedPanel
          title={s.subject}
          minimized={minimized}
          expanded={expanded}
          onMinimize={() => setMinimized(!minimized)}
          onExpand={() => setExpanded(!expanded)}
          onClose={() => setPopped(false)}
          labels={{ minimize: t("mail.compose.minimize"), restore: t("mail.compose.restore"), expand: t("mail.compose.expand"), collapse: t("mail.compose.collapse"), close: t("mail.reply.moveBack") }}
          footer={actions}
          testID="mail-reply-box"
        >
          <Stack gap="2" paddingY="2" grow>
            <Stack direction="horizontal" align="center" gap="2">
              {kindMenu}
              {openRecipients ? null : <Stack grow>{summary}</Stack>}
            </Stack>
            {fields}
            {subject}
            {message}
            {notes}
          </Stack>
          {dialogs}
        </DockedPanel>
      </>
    );
  }
  return (
    <Stack radius="lg" border="border.strong" background="bg.elevated" keepInView testID="mail-reply-box">
      <Stack direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
        {kindMenu}
        {openRecipients ? (
          <>
            <Text variant="footnote" color="text.tertiary">
              {t("mail.compose.from")}
            </Text>
            {fromPicker}
            <Stack grow />
            {bccLink}
          </>
        ) : (
          <Stack grow>{summary}</Stack>
        )}
        <IconButton icon="externalLink" size="sm" accessibilityLabel={t("mail.reply.popOut")} onPress={() => setPopped(true)} testID="mail-reply-pop" />
      </Stack>
      {openRecipients ? (
        <Stack paddingX="3" paddingY="1.5">
          {fields}
        </Stack>
      ) : null}
      <Divider />
      <Stack gap="2" padding="3">
        {subject}
        {message}
        {notes}
      </Stack>
      <Stack paddingX="3" paddingY="2">
        {actions}
      </Stack>
      {dialogs}
    </Stack>
  );
}

/** Gmail's "•••": shows quoted text that is hidden. */
export function QuoteToggle({ onPress, testID }: { onPress: () => void; testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal">
      <Pressable radius="xs" onPress={onPress} accessibilityLabel={t("mail.showQuoted")} testID={testID}>
        <Stack paddingX="2" radius="xs" background="chip.bg">
          <Text variant="footnote" weight="bold" color="text.secondary">
            •••
          </Text>
        </Stack>
      </Pressable>
    </Stack>
  );
}
